/**
 * Contact CSV export builders.
 *
 * Includes the "Reach App Template" format — a CSV shaped exactly for import
 * into the Reach texting app (see docs/Reach Docs.md):
 *
 *   - Lowercase headers, in order: first, last, nickname, relationship,
 *     address, via, translation
 *   - `address` is the only mandatory column (phone number for texting)
 *   - `via` must be one of: text, imessage, email, gvoice, whatsapp,
 *     whatsapp-business — we always emit `text` since exports feed SMS lists
 *   - NO UTF-8 BOM: Reach matches columns by header name, and a BOM would
 *     corrupt the `first` header
 *
 * Also includes the standard contacts CSV builder (legacy 8-column format),
 * now generated with PapaParse so commas/quotes/newlines in values no longer
 * break rows.
 *
 * All builders are pure (no DOM) so they can be unit tested directly.
 */

import Papa from 'papaparse';
import { normalizePhone } from './contactNormalization';

/** Reach import columns, exact lowercase header names and order. */
export const REACH_COLUMNS = ['first', 'last', 'nickname', 'relationship', 'address', 'via', 'translation'];

/** Legacy standard export columns (headers preserved for compatibility). */
export const STANDARD_COLUMNS = ['firstName', 'lastName', 'email', 'phone', 'company', 'jobTitle', 'source', 'tags'];

/**
 * Build Reach import rows from camelCase contact objects.
 *
 * - `address` comes from the primary phone, falling back to the secondary
 *   phone, normalized to digits (10-digit US format). Contacts with no
 *   usable phone are skipped — Reach requires `address` and these exports
 *   feed texting lists.
 * - Rows sharing the same normalized phone are deduped (first occurrence
 *   wins) so Reach doesn't create duplicate contacts in the group.
 * - `relationship` maps from the contact's primary contact type, which makes
 *   it available as a dynamic value in Reach messages.
 *
 * @param {Array<Object>} contacts camelCase contacts (transformContactToCamelCase shape)
 * @returns {{ rows: Array<Object>, skippedNoPhone: number, duplicatesRemoved: number }}
 */
export function buildReachRows(contacts) {
  const rows = [];
  const seenAddresses = new Set();
  let skippedNoPhone = 0;
  let duplicatesRemoved = 0;

  for (const contact of contacts || []) {
    const address = normalizePhone(contact.phone) || normalizePhone(contact.secondaryPhone);
    if (!address) {
      skippedNoPhone += 1;
      continue;
    }
    if (seenAddresses.has(address)) {
      duplicatesRemoved += 1;
      continue;
    }
    seenAddresses.add(address);

    rows.push({
      first: contact.firstName || '',
      last: contact.lastName || '',
      nickname: '',
      relationship: contact.primaryContactType || '',
      address,
      via: 'text',
      translation: '',
    });
  }

  return { rows, skippedNoPhone, duplicatesRemoved };
}

/**
 * Serialize Reach rows to CSV. Header order is fixed to REACH_COLUMNS and
 * PapaParse handles all quoting/escaping. No BOM is prepended (see module
 * doc — a BOM would break Reach's header matching).
 *
 * Uses Papa's explicit { fields, data } form so the header row is emitted
 * even when there are zero data rows.
 *
 * @param {Array<Object>} rows output of buildReachRows().rows
 * @returns {string} CSV text including the header row
 */
export function buildReachCsv(rows) {
  const data = (rows || []).map((row) => REACH_COLUMNS.map((col) => row[col] ?? ''));
  return Papa.unparse({ fields: REACH_COLUMNS, data });
}

/**
 * Serialize contacts to the legacy standard CSV (same 8 camelCase headers as
 * the original export; tags arrays joined with `;`), with proper escaping.
 *
 * @param {Array<Object>} contacts camelCase contacts
 * @returns {string} CSV text including the header row
 */
export function buildStandardContactsCsv(contacts) {
  const data = (contacts || []).map((contact) =>
    STANDARD_COLUMNS.map((field) => {
      const value = contact[field];
      if (Array.isArray(value)) return value.join(';');
      return value || '';
    })
  );
  return Papa.unparse({ fields: STANDARD_COLUMNS, data });
}

/**
 * Trigger a browser download of a CSV string.
 *
 * @param {string} csvString CSV contents
 * @param {string} filename download filename, e.g. reach-contacts-2026-09-03.csv
 */
export function downloadCsvFile(csvString, filename) {
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
